import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {environment} from '../../../environments/environment';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {IFoodGroup} from './food-group.interface';
import {cloneDeep} from 'lodash';
import {firstValueFrom} from 'rxjs';
import {IFoodGroupFilters} from './food-group-filter.interface';


export interface IRFoodGroup {
  foodGroups: IFoodGroup[];
  selectedId: string;
  count: number;
  filters: IFoodGroupFilters;
}

export namespace FoodGroupAction {
  export class GetById {
    static readonly type = '[Food Group] Get Food Group By Id Action';
    constructor(public clinicId: string, public id: string, public filters?: any) { }
  }

  export class GetByClinicId {
    static readonly type = '[Food Group] Get Food Group By Clinic Id Action';
    constructor(public clinicId: string, public filters?: any, public appendData = false) { }
  }

  export class CountByClinicId {
    static readonly type = '[Food Group] Get Count Food Group By Clinic Id Action';
    constructor(public clinicId: string, public filters?: any) { }
  }

  export class Post {
    static readonly type = '[Food Group] Post Food Group Action';
    constructor(public data: IFoodGroup) { }
  }

  export class Patch {
    static readonly type = '[Food Group] Patch Food Group Action';
    constructor(public id: string, public data: IFoodGroup) { }
  }

  export class DeleteById {
    static readonly type = '[Food Group] Delete Food Group Action';
    constructor(public id: string) { }
  }

  export class SetFilters {
    static readonly type = '[Food Group] Set Food Group List Filters Action';
    constructor(public filters: IFoodGroupFilters) { }
  }

  export class SetSelectedId {
    static readonly type = '[Food Group] Set Selected Id Food Group Action';
    constructor(public id: string) { }
  }
}


@State<IRFoodGroup>({
  name: 'FoodGroupState',
  defaults: {
    foodGroups: [],
    selectedId: null,
    count: 0,
    filters: {
      page: 1,
      search: null,
      tags: null
    },
  }
})

@Injectable()
export class FoodGroupState {
  constructor(
    private baseService: BaseService<any>
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  public static getFoodGroups(state: IRFoodGroup): IFoodGroup[] {
    return state.foodGroups || [];
  }

  @Selector()
  public static getTotalFoodGroups(state: IRFoodGroup): number {
    return state.count;
  }

  @Selector()
  static getSelectedFoodGroupId(state: IRFoodGroup): string {
    return state.selectedId;
  }

  @Selector()
  static getFilters(state: IRFoodGroup): IFoodGroupFilters {
    return state.filters;
  }

  @Selector()
  static getSelectedFoodGroup(state: IRFoodGroup): IFoodGroup {
    return state.foodGroups.find((foodGroup: IFoodGroup) => foodGroup._id === state.selectedId);
  }


  @Action(FoodGroupAction.GetById)
  async getFoodGroupsByIdAction(ctx: StateContext<IRFoodGroup>, action?: FoodGroupAction.GetById): Promise<void> {
    const response: DataBaseServiceResponse<IFoodGroup> = await firstValueFrom(
      this.baseService.get(`${this.url}/food-groups/${action.clinicId}/${action.id}`)
    );
    if (response.error) throw new Error(response.message);

    ctx.patchState({
      foodGroups: [response.entity],
      selectedId: action.id
    });
  }

  @Action(FoodGroupAction.GetByClinicId)
  async getFoodGroupsByClinicIdAction(ctx: StateContext<IRFoodGroup>, action?: FoodGroupAction.GetByClinicId): Promise<void> {
    const response: DataBaseServiceResponse<IFoodGroup[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/food-groups/${action.clinicId}`, void 0, void 0, {params: {...action.filters}})
    );
    if (response.error) throw new Error(response.message);

    ctx.patchState({
      foodGroups: !action.appendData ? response.entity : [...ctx.getState()?.foodGroups || [], ...response.entity],
    });
  }

  @Action(FoodGroupAction.CountByClinicId)
  async getCountFoodGroupsByClinicIdAction(ctx: StateContext<IRFoodGroup>, action?: FoodGroupAction.CountByClinicId): Promise<void> {
    const response: DataBaseServiceResponse<{count: number}> = await firstValueFrom(
      this.baseService.get(`${this.url}/food-groups/${action.clinicId}/count`, void 0, void 0, {params: {...action.filters}})
    );
    if (response.error) throw new Error(response.message);

    ctx.patchState({
      count: response.entity.count,
    });
  }

  @Action(FoodGroupAction.SetFilters)
  async setFoodGroupListFiltersAction(ctx: StateContext<IRFoodGroup>, action?: FoodGroupAction.SetFilters): Promise<void> {
    ctx.patchState({
      filters: action.filters
    });
  }

  @Action(FoodGroupAction.SetSelectedId)
  async setSelectedIdFoodGroupAction(ctx: StateContext<IRFoodGroup>, action?: FoodGroupAction.SetSelectedId): Promise<void> {
    ctx.patchState({
      selectedId: action.id,
    });
  }

  @Action(FoodGroupAction.Post)
  async postFoodGroupsAction(ctx: StateContext<IRFoodGroup>, action?: FoodGroupAction.Post): Promise<void> {
    const response: DataBaseServiceResponse<IFoodGroup> = await firstValueFrom((this.baseService.post(`${this.url}/food-groups`, action.data)));

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      foodGroups: [...(cloneDeep(ctx.getState()).foodGroups || []), response.entity],
      selectedId: response.entity._id
    });
  }

  @Action(FoodGroupAction.Patch)
  async patchFoodGroupsAction(ctx: StateContext<IRFoodGroup>, action?: FoodGroupAction.Patch): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/food-groups/${action.id}`, action.data)
    );
    if (response.error) throw new Error(response.message);

    ctx.patchState({
      selectedId: action.id
    });
  }

  @Action(FoodGroupAction.DeleteById)
  async deleteFoodGroupsAction(ctx: StateContext<IRFoodGroup>, action?: FoodGroupAction.DeleteById): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(`${this.url}/food-groups/${action.id}`)
    );
    if (response.error) throw new Error(response.message);

    ctx.patchState({
      selectedId: null
    });
  }

}
